<template>
  <div class="d-flex flex-column h-100" data-locator="comments-content">
    <div class="comments-header pl-6 py-1 pr-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center w-100">
        <app-icon-ds
          icon="magnifying-glass"
          weight="far"
          size="xs"
        />
        <AppTextField data-locator="search-input"
                      :placeholder="$t(`${TRANSLATION_KEY_PREFIX}.searchInputPlaceholder`)"
                      class="search-input"
                      flat
                      ref="searchInput"
                      v-model.trim="searchQuery"
                      variant="solo"
        />
      </div>
      <AppDownloadFile ref="appDownloadFileRef" :link="exportToPdfLink" />
      <AppMenu :menu="menuItems" data-locator="checklist-comments-menu">
        <template #activator="{ props }">
          <app-button type="icon" v-bind="props" data-locator="other-actions-menu-button">
            <app-icon-ds
              icon="ellipsis-h"
              weight="far"
              size="xs"
            />
          </app-button>
        </template>
      </AppMenu>
    </div>
    <div class="comments-wrapper pa-6 d-flex flex-column row-gap-6" data-locator="comments-list" ref="commentsWrapper">
      <template v-if="isLoadingComments">
        <div class="d-flex flex-column row-gap-4">
          <v-skeleton-loader type="text"></v-skeleton-loader>
          <v-skeleton-loader type="image" class="w-100"></v-skeleton-loader>
        </div>
      </template>
      <template v-else-if="filteredTaskComments.length">
        <template v-for="group, index in filteredTaskComments" :key="index">
          <app-text-divider>
            <span class="text--x-small text--bold">{{ group.date }}</span>
          </app-text-divider>
          <template v-for="comment in group.comments" :key="comment.id">
            <ChecklistPaneCommentsItem :is-fading="commentPendingId === comment.id"
                                      :item="comment" 
                                      :search-query="debouncedSearchQuery"
                                      @delete-comment="deleteComment"
            />
          </template>
        </template>
      </template>
      <template v-else>
        <div class="d-flex  flex-column row-gap-2 align-center justify-center h-100">
          <app-icon-ds
            icon="file-xmark"
            weight="far"
            size="lg"
          />
          <span class="text--bold text--large text-grey-900">{{ $t(`${TRANSLATION_KEY_PREFIX}.noCommentsTitle`) }}</span>
          <p class="text-grey-700 text-center">{{ noCommentsText }}</p>
        </div>
      </template>
    </div>
    <div class="comments-footer pa-6" v-if="canPostComment && !isLoadingComments">
      <ChecklistPaneCommentsForm :cleared="commentPendingId === null"
                                 :disabled="!currentRoom.userRights.canCommentTasks || isTaskPending"
                                 :taggable-groups="taggableGroups"
                                 :taggable-users="taggableUsers"
                                 @post-comment="postComment"
                                 @data-unsaved="onDataUnsaved"
      />
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapActions, mapGetters, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppMenu from '@/common/AppMenu.vue'
import AppTextField from '@/common/AppTextField.vue'
import { isToday, ISOToShortenedDate } from '@/common/utils/dates'
import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import ChecklistPaneCommentsForm from '@/project/checklist/panes/ChecklistPaneCommentsForm.vue'
import ChecklistPaneCommentsItem from '@/project/checklist/panes/ChecklistPaneCommentsItem.vue'
import { GET_TASK_COMMENTS, POST_TASK_COMMENT, DELETE_TASK_COMMENT } from '@/store/modules/checklist/action_types'

const COMMENTS_CONTAINER_SCROLL_TIMER = 500
const SEARCH_QUERY_DEBOUNCE_TIMER = 300
const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistPaneComments'

export default {
  name: 'ChecklistPaneComments',
  components: { AppDownloadFile, AppMenu, AppTextField, ChecklistPaneCommentsForm, ChecklistPaneCommentsItem },
  emits: ['data-unsaved'],
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapState('room', ['currentRoom']),
    ...mapState('checklist', ['checklistPane','deleteTaskCommentPendingIds', 'postTaskCommentPendingIds','taskCommentsPendingIds']),
    canPostComment() {
      return this.currentTask && this.currentRoom.userRights.canCommentTasks
    },
    currentTab() {
      return this.checklistPane.tab
    },
    currentTask() {
      return this.checklistPane.tasks[0]
    },
    exportToPdfLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.currentRoom.mnemo}/downloads/todoTask/${this.currentTask?.id}/comments-pdf`
    },
    filteredTaskComments() {
      if (!this.hasSearchQuery) return this.taskComments

      return this.taskComments.map(group => {
        const filteredComments = group.comments.filter(comment => {
          const rawMessage = escapeHtml(comment.message)

          return (
            rawMessage.toLowerCase().includes(this.debouncedSearchQuery.toLowerCase()) ||
            (comment.user?.fullName.toLowerCase().includes(this.debouncedSearchQuery.toLowerCase()))
          )
        })

        return { ...group, comments: filteredComments }
      }).filter(group => group.comments.length > 0)
    },
    hasSearchQuery() {
      return this.debouncedSearchQuery.length > 1
    },
    menuItems() {
      const menuItems = []
      menuItems.push({
        icon: 'arrow-up-from-line',
        id: 'export-to-pdf',
        title: this.$t(`${TRANSLATION_KEY_PREFIX}.menuItems.exportToPdf`),
        action: () => this.exportToPdf(),
      })

      return menuItems
    },
    isLoadingComments () {
      return this.taskCommentsPending && !this.taskComments.length
    },
    isTaskPending () {
      return this.taskCommentsPending || this.postTaskCommentPending || this.deleteTaskCommentPending
    },
    deleteTaskCommentPending () {
      return this.deleteTaskCommentPendingIds.includes(this.currentTask?.id)
    },
    postTaskCommentPending () {
      return this.postTaskCommentPendingIds.includes(this.currentTask?.id)
    },
    taskCommentsPending () {
      return this.taskCommentsPendingIds.includes(this.currentTask?.id)
    },
    noCommentsText () {
      return this.isCurrentUserPm ? this.$t(`${TRANSLATION_KEY_PREFIX}.noComments`) : this.$t(`${TRANSLATION_KEY_PREFIX}.noCommentsForGuest`)
    },
  },
  data: () => ({
    TRANSLATION_KEY_PREFIX,
    commentPendingId: null,
    searchQuery: '',
    debouncedSearchQuery: '',
    taggableGroups: [],
    taggableUsers: [],
    taskComments: [],
  }),
  watch: {
    currentTask: {
      immediate: true,
      handler (val) {
        if(val) {
          this.getTaskComments()
        }
      },
    },
    currentTab() {
      if(this.currentTab !== 'comments') return
      this.getTaskComments()
    },
    searchQuery: {
      handler: debounce(function (newValue) {
        this.debouncedSearchQuery = newValue
      }, SEARCH_QUERY_DEBOUNCE_TIMER),
      immediate: true,
    },
  },
  methods: {
    ...mapActions('checklist', [GET_TASK_COMMENTS, POST_TASK_COMMENT, DELETE_TASK_COMMENT]),
    async deleteComment (itemId) {
      this.commentPendingId = itemId
      try {
        await this.DELETE_TASK_COMMENT({
          mnemo: this.currentRoom.mnemo,
          taskId: this.currentTask.id,
          commentId: itemId,
        })
        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.deleteTaskCommentSuccess`))
        this.getTaskComments()
      } catch (e) {
        this.commentPendingId = null
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.deleteTaskCommentError`))
      }
    },
    async exportToPdf() {
      await this.$nextTick()
      this.$refs.appDownloadFileRef.submitForm()
    },
    async getTaskComments (scroll = true) {
      try {
        const response = await this.GET_TASK_COMMENTS({
          mnemo: this.currentRoom.mnemo,
          taskId: this.currentTask.id,
          params: this.commentsParams,
        })
        
        this.taskComments = this.groupCommentsByDate(response?.comments) || []
        this.taggableGroups = response?.taggable.groups || []
        this.taggableUsers = response?.taggable.users || []
        
        if (scroll) {
          this.$nextTick(() => {
            this.scrollToCommentsContainerEnd()
          })
        }
      } catch (e) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.getTaskCommentsError`))
      }
    },
    groupCommentsByDate(comments) {
      return comments.reduce((acc, comment) => {
        const date = isToday(comment.createdAt) ? this.$t(`common.today`) : ISOToShortenedDate(comment.createdAt)
        const existingGroup = acc.find(group => group.date === date);
        if (existingGroup) {
          existingGroup.comments.push(comment);
        } else {
          acc.push({ date, comments: [comment] });
        }
        return acc;
      }, []);
    },
    onDataUnsaved (dataUnsaved) {
      this.$emit('data-unsaved', dataUnsaved)
    },
    async postComment (newComment) {
      this.commentPendingId = 0
      if (newComment) {
        try {
          await this.POST_TASK_COMMENT({
            mnemo: this.currentRoom.mnemo,
            taskId: this.currentTask.id,
            data: {
              comment: newComment,
            },
          })
      
          toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.postTaskCommentSuccess`))
          this.$emit('data-unsaved', null)
          this.getTaskComments()
        } catch (e) {
          toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.postTaskCommentError`))
        } finally {
          this.commentPendingId = null
        }
      }
    },
    scrollToCommentsContainerEnd () {
      setTimeout(() => {
        if (this.$refs?.commentsWrapper) {
          this.$refs.commentsWrapper.scrollTo({ top: this.$refs.commentsWrapper.scrollHeight, behavior: "smooth"})
        }
      }, COMMENTS_CONTAINER_SCROLL_TIMER)
    },
  },
};
</script>
<style scoped>
.comments-wrapper {
  height: 100%;
  overflow-y: auto;

  :deep(.v-skeleton-loader__text) {
    margin: 0;
    height: 16px;
    background-color: rgb(var(--v-theme-grey-50));
  }
  :deep(.v-skeleton-loader__image) {
    height: 80px;
  }
}
.comments-header {
  border-bottom: 1px solid rgb(var(--v-theme-grey-100));

  .search-input {
    border: none;
  }
}

:deep(.v-field__input) {
  padding-inline: 8px !important;
}
</style>